import React from "react";
import { useTranslation } from "react-i18next";
import { labelFor } from "@/lib/enumLabels";
import { getIngestRunFailures } from "@/lib/api/ingestRuns";
import { Trash, DownloadSimple, Check, Lock } from "@phosphor-icons/react";

/**
 * v0.4.37 R2 — Production files data table row (was a grid-13
 * log row).
 *
 * Columns:
 *   1. Checkbox-region (Filename + DO-NOT-FILE/DUPLICATE badges)
 *      40% width, white 14px bold.
 *   2. Adapter chip — colour-coded per source. This project ingests
 *        a single source (CLIENT_TEMPLATE → teal); unknown adapters
 *        fall back to a neutral slate chip.
 *   3. Date (ISO YYYY-MM-DD).
 *   4. Rows ingested (right-aligned).
 *   5. Rows failed (right-aligned, red-300 when >0, slate-500 when 0).
 *   6. Status pill (PROD / FIXTURE).
 *   7. Delete (ADMIN) — icon button; a lock icon instead, disabled,
 *      when `run.locked_by_frozen_calc` is set (v0.6.9 — see below).
 *
 * SHA hash hidden by default; surfaced via the native `title=`
 * tooltip on the filename cell so it appears on hover without
 * cluttering the row.
 *
 * v0.6.9 — `run.locked_by_frozen_calc` (from GET /runs/ingest,
 * batch-computed) surfaces a LOCKED badge + swaps the delete button
 * for a disabled lock icon when this upload is referenced by a filed
 * (frozen) calculation — proactively, before the operator tries to
 * delete it and discovers only afterward, via a toast, that nothing
 * happened.
 */
const ADAPTER_COLORS = {
  CLIENT_TEMPLATE: "bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border border-teal-300 dark:border-teal-700",
};

const AdapterChip = ({ adapter }) => {
  const key = (adapter || "").toUpperCase();
  const cls = ADAPTER_COLORS[key]
    || "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700";
  return (
    <span
      data-testid={`run-row-adapter-chip-${key.toLowerCase()}`}
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold ${cls}`}
      // v0.6.12 — printed the raw adapter code, so the most prominent chip
      // on the runs list read "CLIENT_TEMPLATE". enumLabels has had an
      // ADAPTER dictionary the whole time; the colour lookup still keys off
      // the raw code, only the visible words change.
      title={adapter}
    >{labelFor("adapter", (adapter || "").toLowerCase())}</span>
  );
};

export const RunRow = ({
  run: r, isFixture, checked, onToggle, onDelete, onDownload,
  conflictsWithSelection = false,
  // v0.5.0g+2 — when duplicate filenames are collapsed, `earlierCount`
  // is the number of earlier-by-filename siblings this row represents.
  // Rendered as a subtle "+N earlier" badge next to the filename.
  earlierCount = 0,
  // A2-467 — the upload that just arrived: framed and labelled so it can't
  // be missed (operator 2026-10-20), and scrolled into view once.
  justUploaded = false,
}) => {
  const { t } = useTranslation();
  const rowRef = React.useRef(null);
  // A2-507 — the refused rows' reasons, loaded when the operator asks.
  const [whyOpen, setWhyOpen] = React.useState(false);
  const [why, setWhy] = React.useState(null);
  const toggleWhy = async (e) => {
    e.stopPropagation();
    const next = !whyOpen;
    setWhyOpen(next);
    if (next && !why) {
      try {
        setWhy(await getIngestRunFailures(r.id));
      } catch (err) {
        setWhy({ error: err?.message || "Could not load the reasons." });
      }
    }
  };
  const fmt = (n) => Number(n || 0).toLocaleString();
  React.useEffect(() => {
    if (!justUploaded || !rowRef.current?.scrollIntoView) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    rowRef.current.scrollIntoView({ block: "nearest", behavior: reduce ? "auto" : "smooth" });
  }, [justUploaded]);
  const dateStr = r.created_at
    ? new Date(r.created_at).toISOString().slice(0, 10) : "—";
  const sha = r.file_hash || r.id;
  return (
    <>
    <div
      ref={rowRef}
      data-testid={`run-row-${r.id}`}
      data-selected={checked} data-just-uploaded={justUploaded || undefined}
      className={`${justUploaded ? "run-row-just-uploaded " : ""}w-full flex items-center gap-4 px-4 py-3 border-b border-slate-200 dark:border-slate-700 text-xs border-l-4 transition-colors ${
        checked
          ? "bg-teal-50 dark:bg-teal-900/40 border-l-teal-400 dark:border-l-teal-500"
          : "hover:bg-slate-50 dark:bg-slate-950 dark:hover:bg-slate-800/60 border-l-transparent"
      }`}
    >
      <button
        type="button"
        data-testid={`run-row-toggle-${r.id}`}
        onClick={() => onToggle(r.id)}
        className="flex-[2] min-w-0 flex items-center gap-2.5 text-left truncate"
        title={`SHA: ${sha}`}
      >
        {/* v0.6.9 — operator feedback: a background-tint + left-border
            was the only signal a row was selected, easy to miss at a
            glance across a long list. An explicit checkbox-style tick
            makes selection state unambiguous. */}
        <span
          data-testid={`run-row-checkbox-${r.id}`}
          aria-hidden="true"
          className={`flex-none w-4 h-4 rounded border flex items-center justify-center transition-colors ${
            checked
              ? "bg-teal-600 border-teal-600 dark:bg-teal-500 dark:border-teal-500"
              : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600"
          }`}
        >
          {checked && <Check size={11} weight="bold" className="text-white" />}
        </span>
        {/* A2-537 (Antigravity UX review U7): shortened to "UK T" with no way to read the rest */}
        <span title={r.filename} className={`text-sm font-bold text-slate-900 dark:text-slate-50${justUploaded ? " min-w-[8rem] truncate" : ""}`}>{r.filename}</span>
        {justUploaded && (
          <span data-testid={`run-row-just-uploaded-${r.id}`}
            className="shrink-0 ml-2 px-1.5 py-0.5 bg-sky-50 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200 text-[10px] font-semibold border border-sky-300 dark:border-sky-700 rounded-full">
            {t("ui.calculate.just_uploaded", "Just uploaded")}
          </span>
        )}
        {isFixture && (
          <span
            data-testid={`run-fixture-badge-${r.id}`}
            className="ml-2 px-1.5 py-0.5 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-[10px] font-semibold border border-red-200 dark:border-red-800 rounded-full"
          >{t("ui.calculate.do_not_file")}</span>
        )}
        {conflictsWithSelection && (
          <span
            data-testid={`run-duplicate-badge-${r.id}`}
            title={t("ui.upload.duplicate_badge_tooltip")}
            className="ml-2 px-1.5 py-0.5 bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-semibold border border-amber-200 dark:border-amber-800 rounded-full"
          >{t("ui.upload.duplicate_badge")}</span>
        )}
        {r.is_duplicate_of && (
          <span
            data-testid={`run-forced-copy-badge-${r.id}`}
            title={t("ui.upload.reuploaded_badge_tooltip", {
              defaultValue: `Same content as an earlier upload (identical SHA-256 fingerprint). Accepted because someone clicked "Upload again anyway" on the duplicate prompt, OR a test script set force=true. Re-uploads are the main source of clutter in this list — use "Select re-uploads" on the toolbar to clean up.`,
            })}
            className="ml-2 px-1.5 py-0.5 bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 text-[10px] font-semibold border border-rose-200 dark:border-rose-800 rounded-full"
          >{t("ui.upload.reuploaded_badge", { defaultValue: "RE-UPLOADED" })}</span>
        )}
        {/* v0.6.8 — found live 2026-09-05: an operator picked an older
            upload of "Book1.csv" that had no IOSS scheme set instead
            of the correct re-upload that did, and the calc silently
            classified every row as an ordinary local sale instead of
            IOSS — nothing here distinguished the two runs by name
            alone. This badge is the fix: the IOSS scheme declared at
            upload time (routes_ingest.py's `ioss_scheme`) is exactly
            what drives that classification, so show it directly. */}
        {/* A2-507 — the Rows column counts SALES only; a file whose 'Sale or
            purchase' column was set the wrong way showed a small number
            here and its purchases nowhere. */}
        {(r.purchases_accepted || 0) > 0 && (
          <span
            data-testid={`run-split-badge-${r.id}`}
            className="ml-2 px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold border border-slate-300 dark:border-slate-700 rounded-full tabular-nums"
          >{t("ui.calculate.run_row.split", { sales: fmt(r.rows_accepted), purchases: fmt(r.purchases_accepted), defaultValue: `${fmt(r.rows_accepted)} sales · ${fmt(r.purchases_accepted)} purchases` })}</span>
        )}
        {r.ioss_scheme && (
          <span
            data-testid={`run-ioss-badge-${r.id}`}
            title={t("ui.calculate.run_row.ioss_title", { scheme: r.ioss_scheme, defaultValue: `Uploaded with IOSS scheme "${r.ioss_scheme}" — its rows classify as IOSS, not ordinary local sales.` })}
            className="ml-2 px-1.5 py-0.5 bg-violet-50 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 text-[10px] font-semibold border border-violet-200 dark:border-violet-800 rounded-full"
          >IOSS ({r.ioss_scheme})</span>
        )}
        {earlierCount > 0 && (
          <span
            data-testid={`run-earlier-badge-${r.id}`}
            title={t("ui.calculate.run_row.earlier_title", { n: earlierCount, file: r.filename, defaultValue: `${earlierCount} older upload(s) of "${r.filename}" hidden — toggle "Group by filename" off to see them.` })}
            className="ml-2 px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] font-semibold border border-slate-300 dark:border-slate-700 rounded-full"
          >{t("ui.calculate.run_row.earlier_badge", { n: earlierCount, defaultValue: `${earlierCount} older hidden` })}</span>
        )}
        {/* v0.6.9 — found live: an operator tried deleting/bulk-deleting
            uploads referenced by a FROZEN (filed) calculation, which the
            backend correctly refuses ("filed returns are immutable") —
            but the only signal was a toast shown AFTER the attempt, easy
            to miss, with nothing on the row itself explaining why
            beforehand. This badge shows the lock proactively. */}
        {r.locked_by_frozen_calc && (
          <span
            data-testid={`run-locked-badge-${r.id}`}
            title={t("ui.calculate.run_row.locked_title", { ref: r.locked_by_frozen_calc.filing_reference || r.locked_by_frozen_calc.id, defaultValue: `Locked — part of a FILED (frozen) calculation (${
              r.locked_by_frozen_calc.filing_reference || r.locked_by_frozen_calc.id
            }). Filed returns are immutable, so this upload can't be deleted.` })}
            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] font-semibold border border-slate-300 dark:border-slate-700 rounded-full"
          ><Lock size={9} weight="bold" />LOCKED</span>
        )}
      </button>
      <div className="flex-[0_0_auto] flex items-center">
        <AdapterChip adapter={r.adapter} />
      </div>
      <span
        data-testid={`run-row-date-${r.id}`}
        className="flex-[0_0_100px] text-slate-600 dark:text-slate-300 text-right tabular-nums"
      >{dateStr}</span>
      <span className="flex-[0_0_80px] text-right text-slate-800 dark:text-slate-100 font-bold tabular-nums">{r.rows_accepted}</span>
      {r.rows_failed > 0 ? (
        <button
          type="button"
          data-testid={`run-row-failed-${r.id}`}
          onClick={toggleWhy}
          aria-expanded={whyOpen}
          title={t("ui.calculate.run_row.failed_why", { n: fmt(r.rows_failed), defaultValue: `Show why ${fmt(r.rows_failed)} row(s) were not uploaded` })}
          className="flex-[0_0_70px] text-right font-bold text-red-700 dark:text-red-300 tabular-nums underline decoration-dotted underline-offset-2 hover:text-red-900 dark:hover:text-red-200"
        >{fmt(r.rows_failed)}</button>
      ) : (
        <span className="flex-[0_0_70px] text-right font-bold text-slate-600 dark:text-slate-300 tabular-nums">{r.rows_failed}</span>
      )}
      <span
        data-testid={`run-row-status-${r.id}`}
        className={`flex-[0_0_80px] inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
          isFixture
            ? "bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800"
            : "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
        }`}
      >{isFixture ? "FIXTURE" : "PROD"}</span>
      <span className="flex-[0_0_64px] flex items-center justify-end gap-1">
        {onDownload && (
          <button
            type="button"
            data-testid={`run-row-download-${r.id}`}
            onClick={(e) => { e.stopPropagation(); onDownload(r.id, r.filename); }}
            title={t("ui.calculate.run_row.download_title", { file: r.filename, n: r.rows_accepted, defaultValue: `Download "${r.filename}" as ingested (${r.rows_accepted} rows)` })}
            className="p-1 rounded text-slate-500 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/40 transition-colors"
            aria-label={t("ui.calculate.run_row.download_aria", { file: r.filename, defaultValue: `Download ${r.filename}` })}
          ><DownloadSimple size={14} weight="bold" /></button>
        )}
        {onDelete && (
          r.locked_by_frozen_calc ? (
            <span
              data-testid={`run-row-delete-locked-${r.id}`}
              title={t("ui.calculate.run_row.delete_locked_title", { ref: r.locked_by_frozen_calc.filing_reference || r.locked_by_frozen_calc.id, defaultValue: `Locked by a filed calculation (${
                r.locked_by_frozen_calc.filing_reference || r.locked_by_frozen_calc.id
              }) — can't be deleted.` })}
              className="p-1 rounded text-slate-300 dark:text-slate-600 cursor-not-allowed"
              aria-label={t("ui.calculate.run_row.delete_locked_aria", { file: r.filename, defaultValue: `${r.filename} is locked by a filed calculation and can't be deleted` })}
            ><Lock size={14} weight="bold" /></span>
          ) : (
            <button
              type="button"
              data-testid={`run-row-delete-${r.id}`}
              onClick={(e) => { e.stopPropagation(); onDelete(r.id, r.filename); }}
              title={t("ui.calculate.delete_run_tooltip")}
              className="p-1 rounded text-slate-500 dark:text-slate-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/40 transition-colors"
              aria-label={t("ui.calculate.delete_run_aria", { filename: r.filename })}
            ><Trash size={14} weight="bold" /></button>
          )
        )}
      </span>
    </div>
    {whyOpen && (
      <div
        data-testid={`run-row-why-${r.id}`}
        aria-live="polite"
        className="px-12 py-2 border-b border-slate-200 dark:border-slate-700 bg-red-50/60 dark:bg-red-950/30 text-xs text-slate-800 dark:text-slate-100"
      >
        {!why ? t("ui.calculate.run_row.why_loading", { defaultValue: "Loading…" })
          : why.error ? why.error
          : !why.kept ? t("ui.calculate.run_row.why_not_kept", { defaultValue: "The reasons for this upload were not kept — it was uploaded before they were saved. Upload the file again to see them." })
          : (
            <>
              <div className="font-bold mb-1">{t("ui.calculate.run_row.why_title", { n: fmt(why.rows_failed), defaultValue: `Why ${fmt(why.rows_failed)} row(s) were not uploaded` })}</div>
              <ul className="list-disc list-inside space-y-0.5">
                {why.failures.map((f, i) => (
                  <li key={i}>{/^Row \d+/.test(f.reason) ? f.reason : `Row ${f.row}: ${f.reason}`}</li>
                ))}
              </ul>
              {why.dropped > 0 && (
                <div className="mt-1 text-slate-500 dark:text-slate-400">{t("ui.calculate.run_row.why_dropped", { n: fmt(why.dropped), defaultValue: `…and ${fmt(why.dropped)} more not shown.` })}</div>
              )}
              {(why.notes || []).map((n, i) => (
                <div key={`n-${i}`} className="mt-1 text-slate-600 dark:text-slate-300">{n}</div>
              ))}
            </>
          )}
      </div>
    )}
    </>
  );
};
